import { c as _c } from "react/compiler-runtime";
import Head from "next/head";
import "react-toastify/dist/ReactToastify.css";
import { AgreementList } from "../../components/profile/shared/lists/agreementList/AgreementList";
import { DKAgreementList } from "../../components/profile/shared/lists/agreementList/DKAgreementList";
import { AutoGiroAgreement, AvtaleGiroAgreement, DKAgreement, Distribution, VippsAgreement } from "../../models";
import { useAuth0 } from "@auth0/auth0-react";
import { pageBannersContentQuery, useAgreementsDistributions, useAutogiroAgreements, useAvtalegiroAgreements, useDKAgreements, useTaxUnits, useVippsAgreements } from "../../_queries";
import { useMemo, useState } from "react";
import { InfoBox } from "../../components/shared/components/Infobox/Infobox";
import { Clock } from "react-feather";
import AgreementsMenu, { AgreementsMenuOptions } from "../../components/profile/agreements/AgreementsMenu/AgreementsMenu";
import styles from "../../styles/Agreements.module.css";
import { PageContent } from "../../components/profile/layout/PageContent/PageContent";
import { getClient } from "../../lib/sanity.client";
import { groq } from "next-sanity";
import { Spinner } from "../../components/shared/components/Spinner/Spinner";
import { MainHeader } from "../../components/shared/layout/Header/Header";
import { DateTime } from "luxon";
import { withStaticProps } from "../../util/withStaticProps";
import { GeneralPageProps, LayoutType, getAppStaticProps } from "../_app.page";
import { Navbar } from "../../components/shared/components/Navbar/Navbar";
import { token } from "../../token";
import { stegaClean } from "@sanity/client/stega";
import { ConsentState } from "../../middleware.page";
import { CookieBannerQueryResult } from "../../studio/sanity.types";
import { CauseAreaContext } from "../../components/shared/components/Widget/types/WidgetProps";
export async function getAgreementsPagePath() {
  const result = await getClient().fetch<FetchAgreementsPageResult>(fetchAgreementsPage);
  const dashboardSlug = stegaClean(result?.dashboard?.[0]?.dashboard_slug?.current);
  const agreementsSlug = stegaClean(result?.page?.slug?.current);
  if (!dashboardSlug || !agreementsSlug) return null;
  return [dashboardSlug, agreementsSlug];
}
export const AgreementsPage = withStaticProps(async ({
  draftMode = false,
  consentState
}: {
  slug: string[];
  draftMode: boolean;
  consentState: ConsentState;
}) => {
  const appStaticProps = await getAppStaticProps({
    draftMode,
    consentState,
    layout: LayoutType.Profile
  });
  const result = await getClient(draftMode ? token : undefined).fetch<FetchAgreementsPageResult>(fetchAgreementsPage);
  return {
    appStaticProps,
    draftMode,
    preview: draftMode,
    token: draftMode ? token ?? null : null,
    navbarData: await Navbar.getStaticProps({
      dashboard: true,
      draftMode
    }),
    data: {
      result: result,
      query: fetchAgreementsPage,
      queryParams: {}
    }
  } satisfies GeneralPageProps;
})(({
  navbarData,
  data,
  draftMode
}) => {
  const page = data.result.page;
  const cookieBannerConfig = data.result.settings[0].cookie_banner_configuration;
  const locale = data.result.settings[0].main_locale;
  const isDK = locale === "dk";
  if (isDK) {
    return <DKAgreementsPageContent navbarData={navbarData} page={page} cookieBannerConfig={cookieBannerConfig} settings={data.result.settings[0]} causeAreaContexts={data.result.causeAreaContexts ?? []} />;
  }
  return <NordicAgreementsPageContent navbarData={navbarData} page={page} cookieBannerConfig={cookieBannerConfig} settings={data.result.settings[0]} />;
});

// DK-specific agreements page content
const DKAgreementsPageContent: React.FC<{
  navbarData: any;
  page: AgreementsPageData | undefined;
  cookieBannerConfig: CookieBannerQueryResult | undefined;
  settings: {
    title?: string;
  };
  causeAreaContexts: CauseAreaContext[];
}> = t0 => {
  const $ = _c(97);
  const {
    navbarData,
    page,
    cookieBannerConfig,
    settings,
    causeAreaContexts
  } = t0;
  const {
    getAccessTokenSilently,
    user
  } = useAuth0();
  const [selected, setSelected] = useState(AgreementsMenuOptions.ACTIVE_AGREEMENTS);
  const {
    loading: dkAgreementsLoading,
    data: dkAgreements
  } = useDKAgreements(user, getAccessTokenSilently);
  const dkAgreementsLoaded = Array.isArray(dkAgreements);
  let t1;
  if ($[0] !== dkAgreements || $[1] !== dkAgreementsLoaded) {
    t1 = dkAgreementsLoaded ? dkAgreements : [];
    $[0] = dkAgreements;
    $[1] = dkAgreementsLoaded;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const resolvedAgreements = t1;
  let t2;
  if ($[3] !== resolvedAgreements) {
    t2 = Array.from(new Set(resolvedAgreements.map(_temp)));
    $[3] = resolvedAgreements;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activeAgreementKids = t2;
  const shouldFetchDistributions = dkAgreementsLoaded && activeAgreementKids.length > 0;
  const {
    loading: distributionsLoading,
    data: distributions
  } = useAgreementsDistributions(user, getAccessTokenSilently, shouldFetchDistributions, activeAgreementKids);
  const {
    loading: taxUnitsLoading,
    data: taxUnits
  } = useTaxUnits(user, getAccessTokenSilently);
  const taxUnitsLoaded = Array.isArray(taxUnits);
  let activeAgreements;
  let distributionsMap;
  let inactiveAgreements;
  let t3;
  if ($[5] !== cookieBannerConfig || $[6] !== distributions || $[7] !== distributionsLoading || $[8] !== dkAgreementsLoaded || $[9] !== dkAgreementsLoading || $[10] !== navbarData || $[11] !== page || $[12] !== resolvedAgreements || $[13] !== selected || $[14] !== settings || $[15] !== shouldFetchDistributions || $[16] !== taxUnitsLoaded || $[17] !== taxUnitsLoading) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedDistributions = shouldFetchDistributions ? distributions ?? [] : [];
      if (!cookieBannerConfig) {
        t3 = null;
        break bb0;
      }
      if (!page) {
        let t4;
        if ($[22] !== settings.title) {
          t4 = <title>{settings.title} | Aftaler</title>;
          $[22] = settings.title;
          $[23] = t4;
        } else {
          t4 = $[23];
        }
        let t5;
        let t6;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <meta name="description" content="Generated by create next app" />;
          t6 = <link rel="icon" href="/favicon.ico" />;
          $[24] = t5;
          $[25] = t6;
        } else {
          t5 = $[24];
          t6 = $[25];
        }
        let t7;
        if ($[26] !== t4) {
          t7 = <Head>{t4}{t5}{t6}</Head>;
          $[26] = t4;
          $[27] = t7;
        } else {
          t7 = $[27];
        }
        let t8;
        if ($[28] !== navbarData) {
          t8 = <MainHeader hideOnScroll={false}><Navbar {...navbarData} /></MainHeader>;
          $[28] = navbarData;
          $[29] = t8;
        } else {
          t8 = $[29];
        }
        let t9;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <PageContent><div className={styles.container}><h3 className={styles.header}>Missing page configuration</h3></div></PageContent>;
          $[30] = t9;
        } else {
          t9 = $[30];
        }
        let t10;
        if ($[31] !== t7 || $[32] !== t8) {
          t10 = <>{t7}{t8}{t9}</>;
          $[31] = t7;
          $[32] = t8;
          $[33] = t10;
        } else {
          t10 = $[33];
        }
        t3 = t10;
        break bb0;
      }
      if (dkAgreementsLoading || !dkAgreementsLoaded || taxUnitsLoading || !taxUnitsLoaded || shouldFetchDistributions && distributionsLoading) {
        let t4;
        if ($[34] !== settings.title) {
          t4 = <title>{settings.title} | Aftaler</title>;
          $[34] = settings.title;
          $[35] = t4;
        } else {
          t4 = $[35];
        }
        let t5;
        let t6;
        if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <meta name="description" content="Generated by create next app" />;
          t6 = <link rel="icon" href="/favicon.ico" />;
          $[36] = t5;
          $[37] = t6;
        } else {
          t5 = $[36];
          t6 = $[37];
        }
        let t7;
        if ($[38] !== t4) {
          t7 = <Head>{t4}{t5}{t6}</Head>;
          $[38] = t4;
          $[39] = t7;
        } else {
          t7 = $[39];
        }
        let t8;
        if ($[40] !== navbarData) {
          t8 = <Navbar {...navbarData} />;
          $[40] = navbarData;
          $[41] = t8;
        } else {
          t8 = $[41];
        }
        const t9 = page.active_list_configuration?.title;
        const t10 = page.inactive_list_configuration?.title;
        let t11;
        if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = selected_0 => setSelected(selected_0);
          $[42] = t11;
        } else {
          t11 = $[42];
        }
        let t12;
        if ($[43] !== selected || $[44] !== t10 || $[45] !== t9) {
          t12 = <AgreementsMenu selected={selected} activeLabel={t9} inactiveLabel={t10} onChange={t11} />;
          $[43] = selected;
          $[44] = t10;
          $[45] = t9;
          $[46] = t12;
        } else {
          t12 = $[46];
        }
        let t13;
        if ($[47] !== t12 || $[48] !== t8) {
          t13 = <MainHeader hideOnScroll={false}>{t8}{t12}</MainHeader>;
          $[47] = t12;
          $[48] = t8;
          $[49] = t13;
        } else {
          t13 = $[49];
        }
        let t14;
        if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = <PageContent><div className={styles.container}><h3 className={styles.header}>Faste aftaler</h3><div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}><Spinner /></div></div></PageContent>;
          $[50] = t14;
        } else {
          t14 = $[50];
        }
        let t15;
        if ($[51] !== t13 || $[52] !== t7) {
          t15 = <>{t7}{t13}{t14}</>;
          $[51] = t13;
          $[52] = t7;
          $[53] = t15;
        } else {
          t15 = $[53];
        }
        t3 = t15;
        break bb0;
      }
      let t4;
      if ($[54] !== resolvedAgreements) {
        t4 = resolvedAgreements.filter(_temp2);
        $[54] = resolvedAgreements;
        $[55] = t4;
      } else {
        t4 = $[55];
      }
      activeAgreements = t4;
      let t5;
      if ($[56] !== resolvedAgreements) {
        t5 = resolvedAgreements.filter(_temp3);
        $[56] = resolvedAgreements;
        $[57] = t5;
      } else {
        t5 = $[57];
      }
      inactiveAgreements = t5;
      distributionsMap = new Map();
      resolvedDistributions.forEach(distribution => {
        distributionsMap.set(distribution.kid, distribution);
      });
    }
    $[5] = cookieBannerConfig;
    $[6] = distributions;
    $[7] = distributionsLoading;
    $[8] = dkAgreementsLoaded;
    $[9] = dkAgreementsLoading;
    $[10] = navbarData;
    $[11] = page;
    $[12] = resolvedAgreements;
    $[13] = selected;
    $[14] = settings;
    $[15] = shouldFetchDistributions;
    $[16] = taxUnitsLoaded;
    $[17] = taxUnitsLoading;
    $[18] = activeAgreements;
    $[19] = distributionsMap;
    $[20] = inactiveAgreements;
    $[21] = t3;
  } else {
    activeAgreements = $[18];
    distributionsMap = $[19];
    inactiveAgreements = $[20];
    t3 = $[21];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[58] !== settings.title) {
    t4 = <title>{settings.title} | Aftaler</title>;
    $[58] = settings.title;
    $[59] = t4;
  } else {
    t4 = $[59];
  }
  let t5;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <link rel="icon" href="/favicon.ico" />;
    $[60] = t5;
  } else {
    t5 = $[60];
  }
  let t6;
  if ($[61] !== t4) {
    t6 = <Head>{t4}{t5}</Head>;
    $[61] = t4;
    $[62] = t6;
  } else {
    t6 = $[62];
  }
  let t7;
  if ($[63] !== navbarData) {
    t7 = <Navbar {...navbarData} />;
    $[63] = navbarData;
    $[64] = t7;
  } else {
    t7 = $[64];
  }
  const t8 = page.active_list_configuration?.title;
  const t9 = page.inactive_list_configuration?.title;
  let t10;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = selected_1 => setSelected(selected_1);
    $[65] = t10;
  } else {
    t10 = $[65];
  }
  let t11;
  if ($[66] !== selected || $[67] !== t8 || $[68] !== t9) {
    t11 = <AgreementsMenu selected={selected} activeLabel={t8} inactiveLabel={t9} onChange={t10} />;
    $[66] = selected;
    $[67] = t8;
    $[68] = t9;
    $[69] = t11;
  } else {
    t11 = $[69];
  }
  let t12;
  if ($[70] !== t11 || $[71] !== t7) {
    t12 = <MainHeader hideOnScroll={false}>{t7}{t11}</MainHeader>;
    $[70] = t11;
    $[71] = t7;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  const t13 = page?.title;
  let t14;
  if ($[73] !== t13) {
    t14 = <h3 className={styles.header}>{t13}</h3>;
    $[73] = t13;
    $[74] = t14;
  } else {
    t14 = $[74];
  }
  let t15;
  if ($[75] !== activeAgreements || $[76] !== causeAreaContexts || $[77] !== distributionsMap || $[78] !== page.active_list_configuration || $[79] !== selected || $[80] !== taxUnits) {
    t15 = window.innerWidth > 1180 || selected === AgreementsMenuOptions.ACTIVE_AGREEMENTS ? <DKAgreementList agreements={activeAgreements} distributions={distributionsMap} taxUnits={taxUnits} expandable={true} configuration={page?.active_list_configuration} causeAreaContexts={causeAreaContexts} /> : null;
    $[75] = activeAgreements;
    $[76] = causeAreaContexts;
    $[77] = distributionsMap;
    $[78] = page.active_list_configuration;
    $[79] = selected;
    $[80] = taxUnits;
    $[81] = t15;
  } else {
    t15 = $[81];
  }
  let t16;
  if ($[82] !== causeAreaContexts || $[83] !== distributionsMap || $[84] !== inactiveAgreements || $[85] !== page.inactive_list_configuration || $[86] !== selected || $[87] !== taxUnits) {
    t16 = window.innerWidth > 1180 || selected === AgreementsMenuOptions.INACTIVE_AGREEMENTS ? <DKAgreementList agreements={inactiveAgreements} distributions={distributionsMap} taxUnits={taxUnits} expandable={false} configuration={page?.inactive_list_configuration} causeAreaContexts={causeAreaContexts} /> : null;
    $[82] = causeAreaContexts;
    $[83] = distributionsMap;
    $[84] = inactiveAgreements;
    $[85] = page.inactive_list_configuration;
    $[86] = selected;
    $[87] = taxUnits;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  let t17;
  if ($[89] !== t14 || $[90] !== t15 || $[91] !== t16) {
    t17 = <PageContent><div className={styles.container}>{t14}{t15}{t16}</div></PageContent>;
    $[89] = t14;
    $[90] = t15;
    $[91] = t16;
    $[92] = t17;
  } else {
    t17 = $[92];
  }
  let t18;
  if ($[93] !== t12 || $[94] !== t17 || $[95] !== t6) {
    t18 = <>{t6}{t12}{t17}</>;
    $[93] = t12;
    $[94] = t17;
    $[95] = t6;
    $[96] = t18;
  } else {
    t18 = $[96];
  }
  return t18;
};

// NO/SE agreements page content (original logic)
const NordicAgreementsPageContent: React.FC<{
  navbarData: any;
  page: AgreementsPageData | undefined;
  cookieBannerConfig: CookieBannerQueryResult | undefined;
  settings: {
    title?: string;
  };
}> = t0 => {
  const $ = _c(120);
  const {
    navbarData,
    page,
    cookieBannerConfig,
    settings
  } = t0;
  const {
    getAccessTokenSilently,
    user
  } = useAuth0();
  const [selected, setSelected] = useState(AgreementsMenuOptions.ACTIVE_AGREEMENTS);
  const {
    loading: avtaleGiroLoading,
    data: avtaleGiro
  } = useAvtalegiroAgreements(user, getAccessTokenSilently);
  const {
    loading: vippsLoading,
    data: vipps
  } = useVippsAgreements(user, getAccessTokenSilently);
  const {
    loading: autoGiroLoading,
    data: autoGiro
  } = useAutogiroAgreements(user, getAccessTokenSilently);
  let t1;
  bb0: {
    if (!vipps || !avtaleGiro || !autoGiro) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let kidsSet;
    if ($[1] !== autoGiro || $[2] !== avtaleGiro || $[3] !== vipps) {
      kidsSet = new Set();
      [...vipps.map(_temp4), ...avtaleGiro.map(_temp5), ...autoGiro.map(_temp6)].forEach(kid => kidsSet.add(kid));
      $[1] = autoGiro;
      $[2] = avtaleGiro;
      $[3] = vipps;
      $[4] = kidsSet;
    } else {
      kidsSet = $[4];
    }
    let t2;
    if ($[5] !== kidsSet) {
      t2 = Array.from(kidsSet);
      $[5] = kidsSet;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = t2;
  }
  const kids = t1;
  const shouldFetchDistributions = !vippsLoading && !avtaleGiroLoading && !autoGiroLoading && vipps !== undefined && avtaleGiro !== undefined && autoGiro !== undefined && kids.length > 0;
  const {
    loading: distributionsLoading,
    data: distributions
  } = useAgreementsDistributions(user, getAccessTokenSilently, shouldFetchDistributions, kids);
  const {
    loading: taxUnitsLoading,
    data: taxUnits
  } = useTaxUnits(user, getAccessTokenSilently);
  const vippsLoaded = Array.isArray(vipps);
  const avtaleGiroLoaded = Array.isArray(avtaleGiro);
  const autoGiroLoaded = Array.isArray(autoGiro);
  const taxUnitsLoaded = Array.isArray(taxUnits);
  let activeAutogiroAgreements;
  let activeAvtalegiroAgreements;
  let activeVippsAgreements;
  let distributionsMap;
  let t2;
  if ($[7] !== autoGiro || $[8] !== autoGiroLoaded || $[9] !== autoGiroLoading || $[10] !== avtaleGiro || $[11] !== avtaleGiroLoaded || $[12] !== avtaleGiroLoading || $[13] !== cookieBannerConfig || $[14] !== distributions || $[15] !== distributionsLoading || $[16] !== navbarData || $[17] !== page || $[18] !== selected || $[19] !== settings || $[20] !== shouldFetchDistributions || $[21] !== taxUnitsLoaded || $[22] !== taxUnitsLoading || $[23] !== vipps || $[24] !== vippsLoaded || $[25] !== vippsLoading) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb1: {
      const resolvedDistributions = shouldFetchDistributions ? distributions : [];
      const loading = vippsLoading || avtaleGiroLoading || shouldFetchDistributions && distributionsLoading || taxUnitsLoading || autoGiroLoading;
      if (!cookieBannerConfig) {
        t2 = null;
        break bb1;
      }
      if (!page) {
        let t3;
        if ($[31] !== settings.title) {
          t3 = <title>{settings.title} | Avtaler</title>;
          $[31] = settings.title;
          $[32] = t3;
        } else {
          t3 = $[32];
        }
        let t4;
        let t5;
        if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <meta name="description" content="Generated by create next app" />;
          t5 = <link rel="icon" href="/favicon.ico" />;
          $[33] = t4;
          $[34] = t5;
        } else {
          t4 = $[33];
          t5 = $[34];
        }
        let t6;
        if ($[35] !== t3) {
          t6 = <Head>{t3}{t4}{t5}</Head>;
          $[35] = t3;
          $[36] = t6;
        } else {
          t6 = $[36];
        }
        let t7;
        if ($[37] !== navbarData) {
          t7 = <MainHeader hideOnScroll={false}><Navbar {...navbarData} /></MainHeader>;
          $[37] = navbarData;
          $[38] = t7;
        } else {
          t7 = $[38];
        }
        let t8;
        if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <PageContent><div className={styles.container}><h3 className={styles.header}>Missing page configuration</h3></div></PageContent>;
          $[39] = t8;
        } else {
          t8 = $[39];
        }
        let t9;
        if ($[40] !== t6 || $[41] !== t7) {
          t9 = <>{t6}{t7}{t8}</>;
          $[40] = t6;
          $[41] = t7;
          $[42] = t9;
        } else {
          t9 = $[42];
        }
        t2 = t9;
        break bb1;
      }
      if (loading || !vippsLoaded || !avtaleGiroLoaded || !taxUnitsLoaded || !autoGiroLoaded) {
        let t3;
        if ($[43] !== settings.title) {
          t3 = <title>{settings.title} | Avtaler</title>;
          $[43] = settings.title;
          $[44] = t3;
        } else {
          t3 = $[44];
        }
        let t4;
        let t5;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <meta name="description" content="Generated by create next app" />;
          t5 = <link rel="icon" href="/favicon.ico" />;
          $[45] = t4;
          $[46] = t5;
        } else {
          t4 = $[45];
          t5 = $[46];
        }
        let t6;
        if ($[47] !== t3) {
          t6 = <Head>{t3}{t4}{t5}</Head>;
          $[47] = t3;
          $[48] = t6;
        } else {
          t6 = $[48];
        }
        let t7;
        if ($[49] !== navbarData) {
          t7 = <Navbar {...navbarData} />;
          $[49] = navbarData;
          $[50] = t7;
        } else {
          t7 = $[50];
        }
        const t8 = page.active_list_configuration?.title;
        const t9 = page.inactive_list_configuration?.title;
        let t10;
        if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = selected_0 => setSelected(selected_0);
          $[51] = t10;
        } else {
          t10 = $[51];
        }
        let t11;
        if ($[52] !== selected || $[53] !== t8 || $[54] !== t9) {
          t11 = <AgreementsMenu selected={selected} activeLabel={t8} inactiveLabel={t9} onChange={t10} />;
          $[52] = selected;
          $[53] = t8;
          $[54] = t9;
          $[55] = t11;
        } else {
          t11 = $[55];
        }
        let t12;
        if ($[56] !== t11 || $[57] !== t7) {
          t12 = <MainHeader hideOnScroll={false}>{t7}{t11}</MainHeader>;
          $[56] = t11;
          $[57] = t7;
          $[58] = t12;
        } else {
          t12 = $[58];
        }
        let t13;
        if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = <PageContent><div className={styles.container}><h3 className={styles.header}>Faste avtaler</h3><div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}><Spinner /></div></div></PageContent>;
          $[59] = t13;
        } else {
          t13 = $[59];
        }
        let t14;
        if ($[60] !== t12 || $[61] !== t6) {
          t14 = <>{t6}{t12}{t13}</>;
          $[60] = t12;
          $[61] = t6;
          $[62] = t14;
        } else {
          t14 = $[62];
        }
        t2 = t14;
        break bb1;
      }
      let t3;
      if ($[63] !== avtaleGiro) {
        t3 = avtaleGiro.filter(_temp7);
        $[63] = avtaleGiro;
        $[64] = t3;
      } else {
        t3 = $[64];
      }
      activeAvtalegiroAgreements = t3;
      let t4;
      if ($[65] !== autoGiro) {
        t4 = autoGiro.filter(_temp8);
        $[65] = autoGiro;
        $[66] = t4;
      } else {
        t4 = $[66];
      }
      activeAutogiroAgreements = t4;
      let t5;
      if ($[67] !== vipps) {
        t5 = vipps.filter(_temp9);
        $[67] = vipps;
        $[68] = t5;
      } else {
        t5 = $[68];
      }
      activeVippsAgreements = t5;
      distributionsMap = new Map();
      resolvedDistributions.forEach(distribution => {
        distributionsMap.set(distribution.kid, distribution);
      });
    }
    $[7] = autoGiro;
    $[8] = autoGiroLoaded;
    $[9] = autoGiroLoading;
    $[10] = avtaleGiro;
    $[11] = avtaleGiroLoaded;
    $[12] = avtaleGiroLoading;
    $[13] = cookieBannerConfig;
    $[14] = distributions;
    $[15] = distributionsLoading;
    $[16] = navbarData;
    $[17] = page;
    $[18] = selected;
    $[19] = settings;
    $[20] = shouldFetchDistributions;
    $[21] = taxUnitsLoaded;
    $[22] = taxUnitsLoading;
    $[23] = vipps;
    $[24] = vippsLoaded;
    $[25] = vippsLoading;
    $[26] = activeAutogiroAgreements;
    $[27] = activeAvtalegiroAgreements;
    $[28] = activeVippsAgreements;
    $[29] = distributionsMap;
    $[30] = t2;
  } else {
    activeAutogiroAgreements = $[26];
    activeAvtalegiroAgreements = $[27];
    activeVippsAgreements = $[28];
    distributionsMap = $[29];
    t2 = $[30];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[69] !== vipps) {
    t3 = vipps.filter(_temp10);
    $[69] = vipps;
    $[70] = t3;
  } else {
    t3 = $[70];
  }
  const vippsPending = t3;
  let t4;
  if ($[71] !== avtaleGiro) {
    t4 = avtaleGiro.filter(_temp11);
    $[71] = avtaleGiro;
    $[72] = t4;
  } else {
    t4 = $[72];
  }
  const avtalegiroPending = t4;
  let t5;
  if ($[73] !== autoGiro) {
    t5 = autoGiro.filter(_temp12);
    $[73] = autoGiro;
    $[74] = t5;
  } else {
    t5 = $[74];
  }
  const autoGiroPending = t5;
  const pendingCount = vippsPending.length + avtalegiroPending.length + autoGiroPending.length;
  let t6;
  if ($[75] !== settings.title) {
    t6 = <title>{settings.title} | Avtaler</title>;
    $[75] = settings.title;
    $[76] = t6;
  } else {
    t6 = $[76];
  }
  let t7;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <link rel="icon" href="/favicon.ico" />;
    $[77] = t7;
  } else {
    t7 = $[77];
  }
  let t8;
  if ($[78] !== t6) {
    t8 = <Head>{t6}{t7}</Head>;
    $[78] = t6;
    $[79] = t8;
  } else {
    t8 = $[79];
  }
  let t9;
  if ($[80] !== navbarData) {
    t9 = <Navbar {...navbarData} />;
    $[80] = navbarData;
    $[81] = t9;
  } else {
    t9 = $[81];
  }
  const t10 = page.active_list_configuration?.title;
  const t11 = page.inactive_list_configuration?.title;
  let t12;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = selected_1 => setSelected(selected_1);
    $[82] = t12;
  } else {
    t12 = $[82];
  }
  let t13;
  if ($[83] !== selected || $[84] !== t10 || $[85] !== t11) {
    t13 = <AgreementsMenu selected={selected} activeLabel={t10} inactiveLabel={t11} onChange={t12} />;
    $[83] = selected;
    $[84] = t10;
    $[85] = t11;
    $[86] = t13;
  } else {
    t13 = $[86];
  }
  let t14;
  if ($[87] !== t13 || $[88] !== t9) {
    t14 = <MainHeader hideOnScroll={false}>{t9}{t13}</MainHeader>;
    $[87] = t13;
    $[88] = t9;
    $[89] = t14;
  } else {
    t14 = $[89];
  }
  const t15 = page?.title;
  let t16;
  if ($[90] !== t15) {
    t16 = <h3 className={styles.header}>{t15}</h3>;
    $[90] = t15;
    $[91] = t16;
  } else {
    t16 = $[91];
  }
  let t17;
  if ($[92] !== page.pending_agreements_box_configuration || $[93] !== pendingCount) {
    t17 = pendingCount >= 1 && <InfoBox><header><Clock size={24} color="black" />{pendingCount === 1 ? page?.pending_agreements_box_configuration.single_pending_agreement_title : page?.pending_agreements_box_configuration.multiple_pending_agreements_title_template.replace("{{count}}", pendingCount.toString())}</header><p>{pendingCount === 1 ? page?.pending_agreements_box_configuration.single_pending_agreement_text : page?.pending_agreements_box_configuration.multiple_pending_agreements_text_template.replace("{{count}}", pendingCount.toString())}</p></InfoBox>;
    $[92] = page.pending_agreements_box_configuration;
    $[93] = pendingCount;
    $[94] = t17;
  } else {
    t17 = $[94];
  }
  let t18;
  if ($[95] !== activeAutogiroAgreements || $[96] !== activeAvtalegiroAgreements || $[97] !== activeVippsAgreements || $[98] !== distributionsMap || $[99] !== page.active_list_configuration || $[100] !== selected || $[101] !== taxUnits) {
    t18 = window.innerWidth > 1180 || selected === AgreementsMenuOptions.ACTIVE_AGREEMENTS ? <AgreementList vipps={activeVippsAgreements} avtalegiro={activeAvtalegiroAgreements} autogiro={activeAutogiroAgreements} distributions={distributionsMap} taxUnits={taxUnits} expandable={true} configuration={page?.active_list_configuration} /> : null;
    $[95] = activeAutogiroAgreements;
    $[96] = activeAvtalegiroAgreements;
    $[97] = activeVippsAgreements;
    $[98] = distributionsMap;
    $[99] = page.active_list_configuration;
    $[100] = selected;
    $[101] = taxUnits;
    $[102] = t18;
  } else {
    t18 = $[102];
  }
  let t19;
  if ($[103] !== autoGiro || $[104] !== avtaleGiro || $[105] !== distributionsMap || $[106] !== page.inactive_list_configuration || $[107] !== selected || $[108] !== taxUnits || $[109] !== vipps) {
    t19 = window.innerWidth > 1180 || selected === AgreementsMenuOptions.INACTIVE_AGREEMENTS ? <AgreementList vipps={vipps.filter(_temp13)} avtalegiro={avtaleGiro.filter(_temp14)} autogiro={autoGiro.filter(_temp15)} distributions={distributionsMap} taxUnits={taxUnits} expandable={false} configuration={page?.inactive_list_configuration} /> : null;
    $[103] = autoGiro;
    $[104] = avtaleGiro;
    $[105] = distributionsMap;
    $[106] = page.inactive_list_configuration;
    $[107] = selected;
    $[108] = taxUnits;
    $[109] = vipps;
    $[110] = t19;
  } else {
    t19 = $[110];
  }
  let t20;
  if ($[111] !== t16 || $[112] !== t17 || $[113] !== t18 || $[114] !== t19) {
    t20 = <PageContent><div className={styles.container}>{t16}{t17}{t18}{t19}</div></PageContent>;
    $[111] = t16;
    $[112] = t17;
    $[113] = t18;
    $[114] = t19;
    $[115] = t20;
  } else {
    t20 = $[115];
  }
  let t21;
  if ($[116] !== t14 || $[117] !== t20 || $[118] !== t8) {
    t21 = <>{t8}{t14}{t20}</>;
    $[116] = t14;
    $[117] = t20;
    $[118] = t8;
    $[119] = t21;
  } else {
    t21 = $[119];
  }
  return t21;
};
type AgreementsPageData = {
  title?: string;
  pending_agreements_box_configuration: {
    single_pending_agreement_title: string;
    multiple_pending_agreements_title_template: string;
    single_pending_agreement_text: string;
    multiple_pending_agreements_text_template: string;
  };
  active_list_configuration?: any;
  inactive_list_configuration?: any;
  slug?: {
    current?: string;
  };
};
type FetchAgreementsPageResult = {
  settings: Array<{
    title?: string;
    main_locale?: "no" | "sv" | "dk" | "en" | "et";
    cookie_banner_configuration?: CookieBannerQueryResult;
  }>;
  dashboard: Array<{
    dashboard_slug?: {
      current?: string;
    };
  }>;
  page?: AgreementsPageData;
  causeAreaContexts?: CauseAreaContext[];
};
const fetchAgreementsPage = groq`
{
  "settings": *[_type == "site_settings"] {
    title,
    main_locale,
    ${pageBannersContentQuery}
  },
  "dashboard": *[_id == "dashboard"] {
    dashboard_slug {
      current
    },
  },
  "causeAreaContexts": *[_type == "donationwidget"][0].cause_area_display_config.cause_area_contexts,
  "page": *[_id == "agreements"][0] {
    ...,
    active_list_configuration {
      ...,
      details_configuration {
        ...,
        date_selector_configuration->{
          ...,
        }
      }
    },
    slug {
      current
    }
  }
}
`;
function _temp(agreement) {
  return agreement.kid;
}
function _temp2(agreement_0) {
  return !agreement_0.cancelled;
}
function _temp3(agreement_1) {
  return agreement_1.cancelled;
}
function _temp4(a) {
  return a.KID;
}
function _temp5(a_0) {
  return a_0.KID;
}
function _temp6(a_1) {
  return a_1.KID;
}
function _temp7(agreement) {
  return agreement.active === 1;
}
function _temp8(agreement_0) {
  return agreement_0.active === true;
}
function _temp9(agreement_1) {
  return agreement_1.status === "ACTIVE";
}
function _temp10(agreement_2) {
  return agreement_2.status === "PENDING" && DateTime.fromISO(agreement_2.timestamp_created).diff(DateTime.now(), "days").days > -7;
}
function _temp11(agreement_3) {
  return agreement_3.active === 0 && agreement_3.cancelled === null && DateTime.fromISO(agreement_3.created).diff(DateTime.now(), "days").days > -7;
}
function _temp12(agreement_4) {
  return !agreement_4.active && agreement_4.cancelled === null && DateTime.fromISO(agreement_4.created).diff(DateTime.now(), "days").days > -7;
}
function _temp13(agreement_5) {
  return agreement_5.status !== "ACTIVE";
}
function _temp14(agreement_6) {
  return agreement_6.cancelled !== null;
}
function _temp15(agreement_7) {
  return agreement_7.cancelled !== null;
}